import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  useLobbySchedule, useLobbyDayEvent, useSaveLobbySchedule, useSetAttendance,
  useSetAssignmentRole, useTagMeStaff, useUntagMeStaff, useRecordMeetingOutcome,
} from './useLobbyDay'
import {
  assignAttendee, unassignAttendee, moveAssignment as moveAssignmentOp, addMeeting, removeMeeting,
  rescheduleMeeting, setMeetingAsk as setMeetingAskOp, setMeetingRoom as setMeetingRoomOp,
  setMeetingStatus as setMeetingStatusOp, setAssignmentAttendance,
  setMeetingWithWhom as setMeetingWithWhomOp, setAssignmentRole, setMeetingStaff, setMeetingOutcome,
} from '../components/lobbyDay/scheduler/lobbyScheduleEditorOps'
import { clampEndToDay } from '../components/lobbyDay/scheduler/timelineLayout'
import { buildSaveRequest, isNewId } from '../components/lobbyDay/scheduler/lobbyScheduleUtils'
import { suggestConstituentAssignments } from '../components/lobbyDay/scheduler/lobbyScheduleResolver'
import { draftKey, serverFingerprint, serializeDraft, parseDraft } from '../components/lobbyDay/scheduler/scheduleDraft'
import { useTargetCoverage } from './useLobbyRecruit'
import { effectiveTargetRows } from '../components/lobbyDay/recruit/recruitCoverage'
import { composeAutoBuild, eventDays, type AutoBuildSummary, type ItineraryTargetInput } from '../components/lobbyDay/scheduler/itineraryBuilder'
import type {
  ScheduleMeetingDto, LobbyMeetingStatus, AttendanceStatus, MeetingWith, MeetingRole, RecordOutcomeRequest,
} from '../types/lobbyDay'

function isConflictError(err: unknown): boolean {
  return (err as { response?: { status?: number } })?.response?.status === 409
}

// Day-of fields persisted out-of-band (immediate PUTs, not the schedule save) must not register as
// schedule edits: attendance + role on assignments; outcome, attendee-report count and staff tags
// on meetings. They are stripped before the dirty-compare JSON, so a No-show / Make lead / staff
// tag / report never marks the schedule dirty nor stomps unsaved edits via the hydration effect.
function serializeIgnoringOutOfBand(meetings: ScheduleMeetingDto[]): string {
  return JSON.stringify(meetings.map(({
    askResponse: _ar, responseNotes: _rn, attendeeReportCount: _rc, staff: _s, ...m
  }) => ({
    ...m, assignments: m.assignments.map(({ attendance: _a, role: _r, ...rest }) => rest),
  })))
}

export function useLobbyScheduleEditor(eventId: string) {
  const { data: schedule, isLoading, error } = useLobbySchedule(eventId)
  const { data: event } = useLobbyDayEvent(eventId)
  const saveMutation = useSaveLobbySchedule(eventId)
  const { data: coverage } = useTargetCoverage(eventId)
  const queryClient = useQueryClient()

  const [localMeetings, setLocalMeetings] = useState<ScheduleMeetingDto[]>([])
  const [selectedDate, setSelectedDate] = useState<string | null>(null)

  // Which eventId `localMeetings` has actually been hydrated for. Set via setState inside the
  // SAME hydration-effect callback that calls setLocalMeetings below, so React batches them
  // together: no render can ever observe `hydrated === true` while `localMeetings` is still the
  // pre-hydration `[]`. A ref would not have this property — a ref mutates synchronously, so a
  // ref set inside this effect would already read true when a later effect in this same commit
  // (the persistence effect) runs, even though that effect's closure still has `localMeetings`
  // from the stale pre-hydration render.
  const [hydratedForEventId, setHydratedForEventId] = useState<string | null>(null)
  const hydrated = hydratedForEventId === eventId

  // Snapshot whatever draft was sitting in storage before anything else in this hook (or React,
  // via effects) can touch it. Recovery decisions read only this frozen snapshot, never live
  // storage. Deliberately a ref mutated during render, not a `useMemo` — React documents
  // `useMemo` as a performance hint it may discard and recompute even with unchanged deps, and
  // this snapshot is now the load-bearing read path for a data-loss-prevention feature (Finding
  // 1 below), not a mere optimization. The ref itself is never discarded; the conditional guard
  // (only overwrite when `eventId` actually changed) reproduces the "re-read only on eventId
  // change" behaviour deterministically. Safe under StrictMode's double-render: the condition
  // is false on the second pass since the ref was already set on the first.
  const draftSnapshotRef = useRef<{ eventId: string; raw: string | null } | null>(null)
  if (draftSnapshotRef.current === null || draftSnapshotRef.current.eventId !== eventId) {
    draftSnapshotRef.current = { eventId, raw: sessionStorage.getItem(draftKey(eventId)) }
  }
  const draftSnapshot = draftSnapshotRef.current.raw

  const [draftDismissed, setDraftDismissed] = useState(false)
  useEffect(() => { setDraftDismissed(false) }, [eventId])

  const [conflict, setConflict] = useState(false)
  useEffect(() => { setConflict(false) }, [eventId])

  // Sync server → local when data arrives/changes (mirrors LobbyScheduler.tsx:84-89).
  useEffect(() => {
    if (schedule) {
      setLocalMeetings(schedule.meetings)
      setSelectedDate((prev) => prev ?? schedule.startDate)
      setHydratedForEventId(eventId)
    }
  }, [schedule, eventId])

  const legislatorById = useMemo(
    () => new Map((schedule?.legislators ?? []).map((l) => [l.legislatorId, l] as const)), [schedule])
  const attendeeById = useMemo(
    () => new Map((schedule?.attendees ?? []).map((a) => [a.attendeeId, a] as const)), [schedule])

  const autoBuildTargets = useMemo<ItineraryTargetInput[]>(() => {
    const targetAll = event?.targetAllLegislators ?? true
    const eff = effectiveTargetRows(coverage ?? [], targetAll) // coverage is already priority-first
    const roomByLeg = new Map((schedule?.legislators ?? []).map((l) => [l.legislatorId, l.officeRoom]))
    return eff.map((r) => ({
      legislatorId: r.legislatorId,
      isPriority: r.isPriority,
      officeRoom: roomByLeg.get(r.legislatorId) ?? null,
    }))
  }, [coverage, event, schedule])

  const autoBuildTargetCount = useMemo(() => {
    const withMeeting = new Set(localMeetings.map((m) => m.legislatorId))
    return autoBuildTargets.filter((t) => !withMeeting.has(t.legislatorId)).length
  }, [autoBuildTargets, localMeetings])

  const isDirty = useMemo(
    () => !!schedule && serializeIgnoringOutOfBand(localMeetings) !== serializeIgnoringOutOfBand(schedule.meetings),
    [localMeetings, schedule])

  // applyOutOfBand reads dirtiness through this ref (not the `isDirty` value in a callback's
  // closure) so the check reflects the state at PUT-resolution time, not at tap.
  const attendanceMutation = useSetAttendance()
  const roleMutation = useSetAssignmentRole(eventId)
  const tagMutation = useTagMeStaff()
  const untagMutation = useUntagMeStaff()
  const outcomeMutation = useRecordMeetingOutcome(eventId)
  const isDirtyRef = useRef(false)
  useEffect(() => { isDirtyRef.current = isDirty }, [isDirty])

  // The one write path for every out-of-band day-of value (DB-15): apply the op to localMeetings,
  // and keep the server cache consistent for a remount within staleTime — but ONLY when there are
  // no unsaved schedule edits, since patching the schedule query re-seeds localMeetings (the
  // hydration effect) and would stomp staged edits. When dirty, localMeetings already shows the
  // value and it reconciles on the next Save/refetch.
  const applyOutOfBand = useCallback((mutator: (ms: ScheduleMeetingDto[]) => ScheduleMeetingDto[]) => {
    setLocalMeetings(mutator)
    if (!isDirtyRef.current) {
      queryClient.setQueryData(['lobby-day', 'schedule', eventId], (old: typeof schedule | undefined) =>
        old ? { ...old, meetings: mutator(old.meetings) } : old)
    }
  }, [queryClient, eventId])

  const fingerprint = useMemo(
    () => (schedule ? serverFingerprint(schedule.meetings) : ''), [schedule])

  // Recovery is decided entirely from the frozen `draftSnapshot`, never from live storage
  // (which the persistence effect below is actively rewriting/clearing as the user edits).
  // Computed ahead of that effect so the effect can read `recoverableDraft` and hold off
  // clearing storage while there's still an undecided draft on offer.
  const recoveredMeetings = useMemo(
    () => (hydrated ? parseDraft(draftSnapshot, fingerprint) : null),
    [hydrated, draftSnapshot, fingerprint])

  const recoverableDraft = hydrated && recoveredMeetings !== null && !draftDismissed

  // Persist staged edits so a browser Back / refresh / crash doesn't lose them. Gated on
  // `hydrated`: without it, the render where `schedule` has just arrived but `localMeetings`
  // is still the pre-hydration `[]` would register as dirty and overwrite a real stored draft
  // with an empty one, then delete it outright on the very next (truly hydrated) render.
  //
  // The not-dirty branch must NOT clear storage while `recoverableDraft` is true: that is
  // precisely the render on which the banner appears and asks the user to decide, and clearing
  // storage at that moment would leave the stored draft alive only in this component instance's
  // memory — a refresh before the user clicks Restore or Discard would silently lose it with no
  // banner and no trace. Storage is cleared only in the genuinely no-undecided-draft case (no
  // stored draft, one that doesn't match, or one already restored/discarded); `restoreDraft` and
  // `discardDraft` are the only paths allowed to end a *pending* draft's life in storage.
  useEffect(() => {
    if (!schedule || !hydrated) return
    if (isDirty) {
      sessionStorage.setItem(draftKey(eventId), serializeDraft(localMeetings, fingerprint))
    } else if (!recoverableDraft) {
      sessionStorage.removeItem(draftKey(eventId))
    }
  }, [eventId, isDirty, localMeetings, fingerprint, schedule, hydrated, recoverableDraft])

  const restoreDraft = useCallback(() => {
    if (recoveredMeetings) setLocalMeetings(recoveredMeetings)
    setDraftDismissed(true)
  }, [recoveredMeetings])

  const discardDraft = useCallback(() => {
    sessionStorage.removeItem(draftKey(eventId))
    setDraftDismissed(true)
  }, [eventId])

  // Browser close/refresh guard (mirrors LobbyScheduler.tsx:107-112).
  useEffect(() => {
    if (!isDirty) return
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault() }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty])

  const assign = useCallback((meetingId: string, attendeeId: string) => {
    const attendee = attendeeById.get(attendeeId)
    setLocalMeetings((prev) => {
      const meeting = prev.find((m) => m.id === meetingId)
      const legislator = meeting ? legislatorById.get(meeting.legislatorId) : undefined
      if (!attendee || !legislator) return prev
      return assignAttendee(prev, meetingId, attendee, legislator)
    })
  }, [attendeeById, legislatorById])

  const unassign = useCallback((meetingId: string, assignmentId: string) => {
    setLocalMeetings((prev) => unassignAttendee(prev, meetingId, assignmentId))
  }, [])

  const move = useCallback((assignmentId: string, fromMeetingId: string, toMeetingId: string, attendeeId: string) => {
    const attendee = attendeeById.get(attendeeId)
    setLocalMeetings((prev) => {
      const target = prev.find((m) => m.id === toMeetingId)
      const legislator = target ? legislatorById.get(target.legislatorId) : undefined
      if (!attendee || !legislator) return prev
      return moveAssignmentOp(prev, assignmentId, fromMeetingId, toMeetingId, attendee, legislator)
    })
  }, [attendeeById, legislatorById])

  const createMeeting = useCallback((meeting: ScheduleMeetingDto) => {
    setLocalMeetings((prev) => addMeeting(prev, meeting))
  }, [])

  const deleteMeeting = useCallback((meetingId: string) => {
    setLocalMeetings((prev) => removeMeeting(prev, meetingId))
  }, [])

  const reschedule = useCallback((meetingId: string, startTime: string, endTime: string) => {
    setLocalMeetings((prev) => rescheduleMeeting(prev, meetingId, startTime, endTime))
  }, [])

  const resize = useCallback((meetingId: string, durationMin: number) => {
    setLocalMeetings((prev) => {
      const m = prev.find((x) => x.id === meetingId)
      if (!m) return prev
      return rescheduleMeeting(prev, meetingId, m.startTime, clampEndToDay(m.startTime, durationMin))
    })
  }, [])

  const setMeetingAsk = useCallback((meetingId: string, askId: string | null) => {
    const title = askId ? (event?.asks.find((a) => a.id === askId)?.title ?? null) : null
    setLocalMeetings((prev) => setMeetingAskOp(prev, meetingId, askId, title))
  }, [event])

  const setMeetingRoom = useCallback((meetingId: string, room: string) => {
    setLocalMeetings((prev) => setMeetingRoomOp(prev, meetingId, room.trim() === '' ? null : room))
  }, [])

  const setMeetingStatus = useCallback((meetingId: string, status: LobbyMeetingStatus) => {
    setLocalMeetings((prev) => setMeetingStatusOp(prev, meetingId, status))
  }, [])

  const setMeetingWithWhom = useCallback((meetingId: string, withWhom: MeetingWith) => {
    setLocalMeetings((prev) => setMeetingWithWhomOp(prev, meetingId, withWhom))
  }, [])

  // Day-of attendance: optimistic local flip + immediate PUT, deliberately OUTSIDE the dirty/save
  // model (attendance is excluded from `isDirty`, so a No-show tap never marks the schedule dirty
  // nor stomps unsaved scheduler edits). On error we revert the local op to the captured value.
  // Depend on the STABLE `attendanceMutation.mutate`, NOT the whole mutation object: TanStack's
  // useMutation returns a fresh object every render, so listing the object would churn this
  // callback's identity every render (backlog 🐞 2026-08-12 render-loop class).
  const attendanceMutate = attendanceMutation.mutate
  const setAttendance = useCallback((meetingId: string, assignmentId: string, status: AttendanceStatus) => {
    // Staged (`new:`) rows have no server id until Save — an immediate PUT could not route. The
    // sheet hides these controls for staged rows; this is the backstop.
    if (isNewId(meetingId) || isNewId(assignmentId)) return
    let prev: AttendanceStatus = 'Unmarked'
    setLocalMeetings((ms) => {
      const a = ms.find((m) => m.id === meetingId)?.assignments.find((x) => x.assignmentId === assignmentId)
      if (a) prev = a.attendance
      return setAssignmentAttendance(ms, meetingId, assignmentId, status)
    })
    attendanceMutate({ meetingId, assignmentId, status }, {
      onSuccess: () => applyOutOfBand((ms) => setAssignmentAttendance(ms, meetingId, assignmentId, status)),
      onError: () => {
        setLocalMeetings((ms) => setAssignmentAttendance(ms, meetingId, assignmentId, prev))
      },
    })
  }, [attendanceMutate, applyOutOfBand])

  // Team lead: optimistic like attendance (Lead demotes the meeting's other lead locally, as the
  // server does). On error every role on that meeting is restored to its captured value.
  const roleMutate = roleMutation.mutate
  const setRole = useCallback((
    meetingId: string, assignmentId: string, role: MeetingRole,
    opts?: { onError?: (err: unknown) => void },
  ) => {
    if (isNewId(meetingId) || isNewId(assignmentId)) return
    let prevRoles = new Map<string, MeetingRole>()
    setLocalMeetings((ms) => {
      const m = ms.find((x) => x.id === meetingId)
      if (m) prevRoles = new Map(m.assignments.map((a) => [a.assignmentId, a.role] as const))
      return setAssignmentRole(ms, meetingId, assignmentId, role)
    })
    roleMutate({ meetingId, assignmentId, role }, {
      onSuccess: () => applyOutOfBand((ms) => setAssignmentRole(ms, meetingId, assignmentId, role)),
      onError: (err) => {
        setLocalMeetings((ms) => ms.map((m) => (m.id !== meetingId ? m : {
          ...m,
          assignments: m.assignments.map((a) => {
            const r = prevRoles.get(a.assignmentId)
            return r && r !== a.role ? { ...a, role: r } : a
          }),
        })))
        opts?.onError?.(err)
      },
    })
  }, [roleMutate, applyOutOfBand])

  // "I'm in this meeting": not optimistic — the server returns the meeting's staff list (with the
  // viewer's display name), which is applied as-is.
  const tagMutate = tagMutation.mutate
  const untagMutate = untagMutation.mutate
  const setMyStaffTag = useCallback((
    meetingId: string, on: boolean, opts?: { onError?: (err: unknown) => void },
  ) => {
    if (isNewId(meetingId)) return
    const mutate = on ? tagMutate : untagMutate
    mutate(meetingId, {
      onSuccess: (staff) => applyOutOfBand((ms) => setMeetingStaff(ms, meetingId, staff)),
      onError: (err) => opts?.onError?.(err),
    })
  }, [tagMutate, untagMutate, applyOutOfBand])

  // Report-back via the existing outcome PUT (which overwrites notes — callers prefill them).
  const outcomeMutate = outcomeMutation.mutate
  const recordOutcome = useCallback((
    meetingId: string, req: RecordOutcomeRequest,
    opts?: { onSuccess?: () => void; onError?: (err: unknown) => void },
  ) => {
    outcomeMutate({ meetingId, data: req }, {
      onSuccess: () => {
        applyOutOfBand((ms) => setMeetingOutcome(ms, meetingId, req.askResponse, req.responseNotes ?? null))
        opts?.onSuccess?.()
      },
      onError: (err) => opts?.onError?.(err),
    })
  }, [outcomeMutate, applyOutOfBand])

  // Depend on the stable `mutate` fn, NOT the whole `saveMutation` object: TanStack Query's
  // useMutation returns a fresh object literal every render (`return { ...result, mutate, ... }`),
  // so listing `saveMutation` here would give `save` a new identity on every render. Both Schedule
  // hosts report `save` upward through a `[...deps, save]` effect that sinks into the event page's
  // `setEditorState`, so a per-render `save` created an unbounded render loop that froze the whole
  // app on Schedule-tab landing (backlog 🐞 2026-08-12). `mutate` is useCallback([observer])-stable.
  const saveMutate = saveMutation.mutate
  const save = useCallback((opts?: { onSuccess?: () => void; onError?: () => void }) => {
    if (!schedule) return
    saveMutate(buildSaveRequest(schedule, localMeetings), {
      onSuccess: () => { setConflict(false); opts?.onSuccess?.() },
      onError: (err) => { if (isConflictError(err)) setConflict(true); opts?.onError?.() },
    })
  }, [schedule, localMeetings, saveMutate])

  const reloadLatest = useCallback(() => {
    setConflict(false)
    queryClient.invalidateQueries({ queryKey: ['lobby-day', 'schedule', eventId] })
  }, [queryClient, eventId])

  // Day-of manual Refresh (no polling, DB-15). A no-op while dirty: a refetch re-seeds
  // localMeetings through the hydration effect and would drop staged edits.
  const refresh = useCallback(() => {
    if (isDirtyRef.current) return
    queryClient.invalidateQueries({ queryKey: ['lobby-day', 'schedule', eventId] })
    queryClient.invalidateQueries({ queryKey: ['lobby-day', 'day-of-leads', eventId] })
  }, [queryClient, eventId])

  const stageSuggestions = useCallback((): { stagedCount: number; meetingsAffected: number } => {
    const r = suggestConstituentAssignments(
      localMeetings, schedule?.attendees ?? [], schedule?.legislators ?? [])
    setLocalMeetings(r.meetings)
    return { stagedCount: r.stagedCount, meetingsAffected: r.meetingsAffected }
  }, [localMeetings, schedule])

  const autoBuildItinerary = useCallback((durationMin: number): AutoBuildSummary => {
    const days = eventDays(schedule?.startDate ?? '', schedule?.endDate ?? null)
    const { meetings, summary } = composeAutoBuild(
      autoBuildTargets, localMeetings, schedule?.attendees ?? [], schedule?.legislators ?? [], days, durationMin)
    setLocalMeetings(meetings)
    return summary
  }, [autoBuildTargets, localMeetings, schedule])

  return {
    schedule,
    legislators: schedule?.legislators ?? [],
    attendees: schedule?.attendees ?? [],
    asks: event?.asks ?? [],
    meetings: localMeetings,
    startDate: schedule?.startDate ?? null,
    endDate: schedule?.endDate ?? null,
    selectedDate: selectedDate ?? schedule?.startDate ?? null,
    setSelectedDate,
    isLoading,
    error,
    isDirty,
    isSaving: saveMutation.isPending,
    assign,
    unassign,
    move,
    createMeeting,
    deleteMeeting,
    reschedule,
    resize,
    setMeetingAsk,
    setMeetingRoom,
    setMeetingStatus,
    setMeetingWithWhom,
    setAttendance,
    setRole,
    setMyStaffTag,
    recordOutcome,
    refresh,
    save,
    stageSuggestions,
    autoBuildTargetCount,
    autoBuildItinerary,
    recoverableDraft,
    restoreDraft,
    discardDraft,
    conflict,
    reloadLatest,
  }
}
